// packages/app/src/components/solutions-agent/SolutionsAgentPage.tsx
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Page,
  Header,
  Content,
  WarningPanel,
} from '@backstage/core-components';
import { useApi, fetchApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import {
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { PipelineSteps } from './PipelineSteps';
import { ChatMessageBubble } from './ChatMessage';
import {
  ChatMessage,
  RunStatus,
  StartRunResponse,
  resolveActiveStep,
} from './types';

const POLL_MS = 5000;

const useStyles = makeStyles(theme => ({
  layout: {
    display: 'flex',
    height: 'calc(100vh - 200px)',
    minHeight: 480,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexWrap: 'wrap',
  },
  resumeField: {
    flex: 1,
    minWidth: 220,
  },
  transcript: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark'
      ? theme.palette.background.default
      : theme.palette.grey[50],
  },
  empty: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(6),
  },
  composer: {
    borderTop: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  startRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  grow: {
    flex: 1,
    minWidth: 180,
  },
  chatRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
  },
  hint: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
}));

function newMsg(role: ChatMessage['role'], text: string, id?: string): ChatMessage {
  return {
    id: id ?? `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    role,
    text,
    at: new Date().toISOString(),
  };
}

export const SolutionsAgentPage = () => {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const classes = useStyles();

  const [url, setUrl] = useState('https://example.com');
  const [notes, setNotes] = useState('');
  const [mockLlm, setMockLlm] = useState(true);
  const [resumeId, setResumeId] = useState('');
  const [input, setInput] = useState('');
  const [runId, setRunId] = useState<string | null>(null);
  const [status, setStatus] = useState<RunStatus | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [polling, setPolling] = useState(false);

  const clarifiedRef = useRef(false);
  const reviewedRef = useRef(false);
  /** How many times we've entered the review gate (0 = first entry). */
  const reviewRoundRef = useRef(0);
  const completedRef = useRef(false);
  const transcriptRef = useRef<HTMLDivElement>(null);

  const getBaseUrl = useCallback(async () => {
    const backendUrl = await discoveryApi.getBaseUrl('backend');
    return backendUrl.replace('/api/backend', '/api/solutions-agent');
  }, [discoveryApi]);

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [messages, status]);

  const append = useCallback((msg: ChatMessage) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  const applyStatus = useCallback(
    (item: RunStatus) => {
      setStatus(item);
      const st = item.status ?? '';

      if (st === 'clarify' && !clarifiedRef.current) {
        clarifiedRef.current = true;
        const qs = item.questions ?? [];
        const body =
          qs.length > 0
            ? `I need a few clarifications before continuing:\n\n${qs
                .map((q, i) => `${i + 1}. ${q}`)
                .join('\n')}\n\nReply in plain language — I'll pass your answers to the pipeline.`
            : 'Clarify gate reached. Reply with any answers or constraints.';
        append(newMsg('assistant', body, `clarify-${item.runId}`));
      }

      if (st === 'review' && !reviewedRef.current) {
        reviewedRef.current = true;
        const isUpdate = reviewRoundRef.current > 0;
        const reviewLines = [
          isUpdate
            ? 'Updated proposal — open link, then ship or revise again.'
            : 'Review gate: Open proposal → then reply **ship** or describe revisions. Session stays open until you ship.',
        ];
        if (item.proposalUrl) {
          reviewLines.push('Use the button below to open the proposal HTML.');
        }
        append(
          newMsg(
            'assistant',
            reviewLines.join(' '),
            `review-${item.runId}-${reviewRoundRef.current}`,
          ),
        );
      }

      if (st === 'complete' && !completedRef.current) {
        completedRef.current = true;
        setPolling(false);
        const lines = ['Brief complete.'];
        if (item.proposalUrl) lines.push(`Proposal HTML is ready.`);
        if (item.zipUrl) lines.push(`Artifacts zip is ready.`);
        else lines.push(`Zip packaging may still be rolling out on this Runtime.`);
        append(newMsg('assistant', lines.join(' '), `complete-${item.runId}`));
      }

      if (st === 'failed' || st === 'error' || item.error) {
        setPolling(false);
        setError(item.error ?? `Run ended with status=${st}`);
      }
    },
    [append],
  );

  const fetchStatus = useCallback(
    async (id: string) => {
      const baseUrl = await getBaseUrl();
      const res = await fetchApi.fetch(`${baseUrl}/runs/${encodeURIComponent(id)}`);
      const data = (await res.json().catch(() => ({}))) as RunStatus & {
        detail?: string;
      };
      if (!res.ok) {
        throw new Error(data.error ?? data.detail ?? `Status failed: ${res.status}`);
      }
      applyStatus({ ...data, runId: data.runId ?? id });
      return data;
    },
    [applyStatus, fetchApi, getBaseUrl],
  );

  useEffect(() => {
    if (!runId || !polling) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        if (!cancelled) await fetchStatus(runId);
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    };
    tick();
    const handle = window.setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [runId, polling, fetchStatus]);

  const resetGateFlags = () => {
    clarifiedRef.current = false;
    reviewedRef.current = false;
    reviewRoundRef.current = 0;
    completedRef.current = false;
  };

  const handleStart = async () => {
    if (!url.trim() || !notes.trim()) {
      setError('URL and notes are required to start a brief.');
      return;
    }
    setError(undefined);
    setBusy(true);
    resetGateFlags();
    setMessages([]);
    setStatus(null);
    try {
      const baseUrl = await getBaseUrl();
      const res = await fetchApi.fetch(`${baseUrl}/runs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: url.trim(),
          notes: notes.trim(),
          mockLlm,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as StartRunResponse;
      if (!res.ok) {
        throw new Error(data.error ?? `Start failed: ${res.status}`);
      }
      setRunId(data.runId);
      setResumeId(data.runId);
      append(
        newMsg(
          'user',
          `Start brief\nURL: ${url.trim()}\nNotes: ${notes.trim()}\nmockLlm: ${mockLlm}`,
        ),
      );
      append(
        newMsg(
          'system',
          `Pipeline started (${data.mockLlm ? 'mock' : 'live'}). runId=${data.runId}`,
        ),
      );
      setPolling(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleAttach = async () => {
    const id = resumeId.trim();
    if (!id) {
      setError('Paste a runId to re-attach.');
      return;
    }
    setError(undefined);
    setBusy(true);
    resetGateFlags();
    setMessages([newMsg('system', `Re-attaching to run ${id}…`)]);
    try {
      setRunId(id);
      const item = await fetchStatus(id);
      append(
        newMsg(
          'system',
          `Attached. status=${item.status ?? '?'} phase=${item.phase ?? '(none)'}`,
        ),
      );
      const st = item.status ?? '';
      if (st !== 'complete' && st !== 'failed' && st !== 'error') {
        setPolling(true);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setRunId(null);
    } finally {
      setBusy(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !runId) return;
    const st = status?.status;
    setInput('');
    append(newMsg('user', text));

    if (st !== 'clarify' && st !== 'review') {
      append(
        newMsg(
          'assistant',
          `Noted. The pipeline is currently "${st ?? 'unknown'}" — it only accepts replies at clarify or review gates.`,
        ),
      );
      return;
    }

    setBusy(true);
    setError(undefined);
    try {
      const baseUrl = await getBaseUrl();
      const body =
        st === 'clarify'
          ? { answers: text }
          : { notes: text.toLowerCase() === 'ship' ? 'ship' : text };
      const res = await fetchApi.fetch(
        `${baseUrl}/runs/${encodeURIComponent(runId)}/resume`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      const data = (await res.json().catch(() => ({}))) as {
        error?: string;
        resumed?: string;
      };
      if (!res.ok) {
        throw new Error(data.error ?? `Resume failed: ${res.status}`);
      }
      if (st === 'clarify') clarifiedRef.current = true;
      if (st === 'review') {
        const isShip = text.toLowerCase() === 'ship';
        if (!isShip) {
          // Allow another review-gate message after polish returns to status=review.
          reviewedRef.current = false;
          reviewRoundRef.current += 1;
        }
        append(
          newMsg(
            'assistant',
            isShip
              ? 'Ship received — finalizing…'
              : 'Revision notes received — polishing. Session stays open until you ship.',
          ),
        );
      } else {
        append(
          newMsg(
            'assistant',
            'Thanks — resuming after clarify. This can take a while (analyze → red team → harden → UI).',
          ),
        );
      }
      setPolling(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const waitingClarify = status?.status === 'clarify';
  const waitingReview = status?.status === 'review';
  const activeStep = resolveActiveStep(status);
  const composerEnabled =
    !!runId && (waitingClarify || waitingReview) && !busy;

  return (
    <Page themeId="tool">
      <Header
        title="Solutions Brief"
        subtitle="AWS solutions-agent pipeline — digest through proposal"
      />
      <Content>
        {error && <WarningPanel title="Error" message={error} />}

        <div className={classes.layout}>
          <PipelineSteps
            activeStep={activeStep}
            status={status?.status}
            phase={status?.phase}
            runId={runId}
          />

          <div className={classes.main}>
            <div className={classes.toolbar}>
              <TextField
                className={classes.resumeField}
                size="small"
                variant="outlined"
                label="Resume runId"
                placeholder="paste past runId"
                value={resumeId}
                onChange={e => setResumeId(e.target.value)}
              />
              <Button
                variant="outlined"
                onClick={handleAttach}
                disabled={busy || !resumeId.trim()}
              >
                Attach
              </Button>
              {polling && (
                <Typography variant="caption" color="textSecondary">
                  Polling every {POLL_MS / 1000}s…
                </Typography>
              )}
            </div>

            <div className={classes.transcript} ref={transcriptRef}>
              {messages.length === 0 && (
                <div className={classes.empty}>
                  <Typography variant="h6">Start a solutions brief</Typography>
                  <Typography variant="body2">
                    Enter a prospect URL and notes below. Mock LLM stays on by
                    default for safe local POC runs (15–45+ min for live).
                  </Typography>
                </div>
              )}
              {messages.map(m => (
                <ChatMessageBubble
                  key={m.id}
                  message={m}
                  proposalUrl={status?.proposalUrl}
                  zipUrl={status?.zipUrl}
                />
              ))}
            </div>

            <div className={classes.composer}>
              {!runId || status?.status === 'complete' ? (
                <>
                  <div className={classes.startRow}>
                    <TextField
                      className={classes.grow}
                      size="small"
                      variant="outlined"
                      label="Prospect URL"
                      value={url}
                      onChange={e => setUrl(e.target.value)}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={mockLlm}
                          onChange={e => setMockLlm(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Mock LLM"
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleStart}
                      disabled={busy || !url.trim() || !notes.trim()}
                    >
                      Start brief
                    </Button>
                  </div>
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Notes / brief"
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    fullWidth
                  />
                  <Typography className={classes.hint}>
                    Proxied via Backstage <code>/api/solutions-agent</code> (server
                    API key). Keep mock on unless live Bedrock is enabled on the
                    Runtime stack.
                  </Typography>
                </>
              ) : (
                <>
                  <Typography className={classes.hint}>
                    {waitingClarify
                      ? 'Clarify gate — answer in natural language.'
                      : waitingReview
                      ? 'Open proposal → ship to finish, or revise notes to polish and review again.'
                      : `Waiting on pipeline (${status?.status ?? '…'} / ${
                          status?.phase ?? '…'
                        }). Long phases can take 15–45+ minutes.`}
                  </Typography>
                  <div className={classes.chatRow}>
                    <TextField
                      className={classes.grow}
                      size="small"
                      variant="outlined"
                      placeholder={
                        waitingClarify
                          ? 'Your clarify answers…'
                          : waitingReview
                          ? 'ship  or  revision notes…'
                          : 'Waiting for a gate…'
                      }
                      value={input}
                      onChange={e => setInput(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (composerEnabled && input.trim()) handleSend();
                        }
                      }}
                      multiline
                      minRows={1}
                      maxRows={6}
                      disabled={!composerEnabled}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSend}
                      disabled={!composerEnabled || !input.trim()}
                    >
                      Send
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Content>
    </Page>
  );
};

export default SolutionsAgentPage;
